import type { LucideIcon } from "lucide-react";
import {
  Clapperboard,
  File,
  FileQuestionMark,
  FileType,
  FolderDot,
  Image,
  Music,
  X,
} from "lucide-react";
import type React from "react";
import { useCallback } from "react";
import type { DataFieldAsset } from "schema/agent";
import { useProject } from "@/features/project";
import { useTab } from "@/features/Tab";
import { SIDE_BY_SIDE_BREAKPOINT, useUI } from "@/features/ui";
import { Field } from "./Field";

export interface AssetFieldProps {
  field: DataFieldAsset;
  onChange: (value: string | null) => void;
}

function getIconForAccept(accept?: string[]): LucideIcon {
  if (!accept || accept.length === 0) return File;

  const first = accept[0];
  if (first.startsWith("image/")) return Image;
  if (first.startsWith("video/")) return Clapperboard;
  if (first.startsWith("audio/")) return Music;
  if (first.startsWith("font/")) return FileType;
  return FileQuestionMark;
}

export const AssetField: React.FC<AssetFieldProps> = ({ field, onChange }) => {
  const { files } = useProject();
  const { startAssetSelection, setShowChat } = useUI();
  const { setViewMode } = useTab();

  const assets = files.assets || [];

  // Get current asset name for display
  const currentAssetId = field.value;
  const currentAsset = currentAssetId ? assets.find((a) => a.id === currentAssetId) : null;
  const currentAssetName = currentAsset?.name;

  const handleBrowseClick = useCallback(() => {
    // On single column mode, close sidebar to reveal the assets panel
    const isSingleColumn = window.innerWidth < SIDE_BY_SIDE_BREAKPOINT;
    if (isSingleColumn) {
      setShowChat(false);
    }
    startAssetSelection({
      fieldLabel: field.label,
      accept: field.accept,
      onSelect: (assetId: string) => {
        onChange(assetId);
        setViewMode("preview");
        // On mobile, reopen sidebar after selection
        if (isSingleColumn) {
          setShowChat(true);
        }
      },
      onCancel: () => {
        setViewMode("preview");
        // On mobile, reopen sidebar after cancel
        if (isSingleColumn) {
          setShowChat(true);
        }
      },
    });
    setViewMode("assets");
  }, [startAssetSelection, field.label, field.accept, onChange, setViewMode, setShowChat]);

  const handleClear = () => {
    onChange(null);
  };

  const FieldIcon = getIconForAccept(field.accept);

  return (
    <Field icon={FieldIcon} label={field.label} visibleAt={field.visibleAt}>
      <div className="flex items-center gap-1">
        {/* Browse button + asset name */}
        <button
          type="button"
          onClick={handleBrowseClick}
          className="max-w-64 flex-1 flex items-center justify-between h-8 md:h-6 bg-slate-900/50 md:text-xs px-2 rounded hover:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-left transition-colors"
        >
          <span className={`truncate ${currentAssetName ? "text-slate-300" : "text-slate-500"}`}>
            {currentAssetName || "Select asset..."}
          </span>
          <FolderDot size={12} className="text-slate-400 shrink-0" />
        </button>

        {/* Clear button - only shown when value exists */}
        {field.value && (
          <button
            type="button"
            onClick={handleClear}
            className="h-8 w-8 md:h-6 md:w-6 flex items-center justify-center bg-slate-900/50 hover:bg-slate-800 rounded transition-colors"
            title="Clear"
          >
            <X size={12} className="text-slate-400" />
          </button>
        )}
      </div>
    </Field>
  );
};
